import { faSignOut } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setAppLoading } from "actions/appStateActions";
import { setPayableSession } from "actions/payableSessionActions";
import { USER_LOGOUT } from "actions/userActions";
import IamApi from "api/IamApi";
import mainLogo from "assets/ez_pay_logo.svg";
import aboutIcon from "assets/icons/about_icon.svg";
import termsIcon from "assets/icons/checkDoc_icon.svg";
import helpIcon from "assets/icons/question_icon.svg";
import privacyIcon from "assets/icons/shield_icon.svg";
import XButton from "assets/icons/x_button.svg";
import { PageLoader } from "components/ezpay/common/page-loader";
import { WelcomeMessageSection } from "components/ezpay/common/welcome-message-section/welcome-message-section";
import { envs } from "config";
import { customStyles } from "constants/customStyles";
import {
  KUBRA_PRIVACY_POLICY_URL,
  KUBRA_TERMS_OF_USE_URL,
} from "constants/externalUrls";
import { COMMON_NAMESPACE, MENU_NAMESPACE } from "constants/i18n-namespaces";
import { Drawer, MenuButton, MenuItem, TextButton } from "kubra-ux-forge";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useOutletContext } from "react-router";
import { useLocation } from "react-router-dom";
import routing from "routing";
import { getBiller } from "selectors";
import { getPayableSession } from "selectors/payableSession";
import { ISession } from "types/session";
import { getLanguageLabel } from "utilities/language";
import AboutModal from "../about-modal/about-modal";
import { HelpModal } from "../help-modal";
import { LanguageMenu } from "../language-menu/language-menu";
import { Feedback } from "./feedback";
import {
  CustomSheet,
  StyledChangeAccountMobileButton,
  StyledHeaderItemGroupDiv,
} from "./styled-components";

export const Menu = (props: any) => {
  const session = useOutletContext<ISession>();
  const payableSession = useSelector(getPayableSession);
  const biller = useSelector(getBiller);
  const { t, i18n } = useTranslation();
  const [menuButtonOpen, setMenuButtonOpen] = useState(false);
  const [aboutModalDisplay, setAboutModalDisplay] = useState(false);
  const [helpModalDisplay, setHelpModalDisplay] = useState(false);
  const [languageModalDisplay, setLanguageModalDisplay] = useState(false);
  const wasVisited = localStorage.getItem("was_visited");
  let firstVisit: boolean;
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogoutClick = async () => {
    dispatch(setAppLoading(true));
    dispatch({ type: USER_LOGOUT });
    dispatch(setPayableSession(undefined));
    session.logout?.();
    IamApi.ezpay.logout();
    navigate(routing.index());
  };

  function handleMenuClick() {
    setMenuButtonOpen(!menuButtonOpen);
  }

  const setAboutModalIsOpenToTrue = () => {
    setAboutModalDisplay(true);
    handleMenuClick();
  };

  const setAboutModalIsOpenToFalse = () => {
    setAboutModalDisplay(false);
  };

  const setHelpModalIsOpenToTrue = () => {
    setHelpModalDisplay(true);
    handleMenuClick();
  };

  const setHelpModalIsOpenToFalse = () => {
    setHelpModalDisplay(false);
  };

  const closeLanguagesModal = () => {
    setLanguageModalDisplay(false);
  };

  useEffect(() => {
    if (location.pathname.includes("terms")) {
      window.open(KUBRA_TERMS_OF_USE_URL, "_blank", "noopener");
    } else if (location.pathname.includes("privacy")) {
      window.open(KUBRA_PRIVACY_POLICY_URL, "_blank", "noopener");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLanguageDisplayValue = (language: string) => {
    return getLanguageLabel(language, "menu", t);
  };

  if (wasVisited === null && location.pathname === routing.index()) {
    firstVisit = true;
    localStorage.setItem("was_visited", "1");
  } else {
    firstVisit = false;
  }

  if (firstVisit) {
    setAboutModalDisplay(true);
  }

  const userEmail =
    session?.account?.processedPayment?.initiator.emailAddress ?? "";
  const isInternalUser = () => {
    return userEmail !== undefined && userEmail.endsWith("@kubra.com");
  };
  const isProduction = () => {
    return process.env.REACT_APP_ENV === envs.PRODUCTION;
  };

  return (
    <>
      <Drawer
        drawerClassnames="drawer"
        open={menuButtonOpen}
        // @ts-ignore
        direction="left"
        overlayClassnames="ez-pay-menu-greyout"
      >
        <div className="ez-pay-menu-dropdown">
          <div className="menu-hamburger-in-dropdown">
            <MenuButton
              onClick={handleMenuClick}
              isOpen={menuButtonOpen}
              title="menu button"
              dataCy="button-menu"
            />
          </div>
          <MenuItem
            onClick={() => setAboutModalIsOpenToTrue()}
            variant="large"
            icon={
              <img
                src={aboutIcon}
                alt="home"
                className="ez-pay-menu-dropdown-icon"
              />
            }
            itemTheme="dark"
            className="ez-pay-menu-dropdown-button"
          >
            {t("about-ez-pay", { ns: MENU_NAMESPACE })}
          </MenuItem>
          <MenuItem
            onClick={() => setHelpModalIsOpenToTrue()}
            variant="large"
            icon={
              <img
                src={helpIcon}
                alt="help"
                className="ez-pay-menu-dropdown-icon"
              />
            }
            itemTheme="dark"
            className="ez-pay-menu-dropdown-button"
          >
            {t("help", { ns: COMMON_NAMESPACE })}
          </MenuItem>
          <MenuItem
            onClick={() =>
              window.open(KUBRA_PRIVACY_POLICY_URL, "_blank", "noopener")
            }
            variant="large"
            icon={
              <img
                src={privacyIcon}
                alt="privacy policy"
                className="ez-pay-menu-dropdown-icon"
              />
            }
            itemTheme="dark"
            className="ez-pay-menu-dropdown-button"
          >
            {t("privacy-policy", { ns: MENU_NAMESPACE })}
          </MenuItem>
          <MenuItem
            onClick={() =>
              window.open(KUBRA_TERMS_OF_USE_URL, "_blank", "noopener")
            }
            variant="large"
            icon={
              <img
                src={termsIcon}
                alt="terms of use"
                className="ez-pay-menu-dropdown-icon"
              />
            }
            itemTheme="dark"
            className="ez-pay-menu-dropdown-button"
          >
            {t("terms-of-use", { ns: MENU_NAMESPACE })}
          </MenuItem>
        </div>
      </Drawer>
      <div className="ez-pay-menu">
        <PageLoader />
        <div className="ez-pay-top-header">
          <div>
            <div className="ez-pay-menu-hamburger">
              <MenuButton
                onClick={handleMenuClick}
                isOpen={menuButtonOpen}
                title="menu button"
                dataCy="button-menu"
              />
            </div>

            <div className="ez-pay-menu-main">
              <span className="ez-pay-menu-logo-helper"></span>
              <img
                src={mainLogo}
                alt="KUBRA EZ-PAY"
                className="ez-pay-menu-logo"
                data-cy="menu-logo"
              />
            </div>
          </div>

          {isInternalUser() && isProduction() && (
            <StyledHeaderItemGroupDiv variant="right">
              <Feedback userEmail={userEmail} />
            </StyledHeaderItemGroupDiv>
          )}

          <div>
            {/* language modal, sheet and button */}
            <LanguageMenu
              isMobile={props.isMobile}
              languageModalDisplay={languageModalDisplay}
              closeLanguagesModal={closeLanguagesModal}
              setLanguageModalDisplay={setLanguageModalDisplay}
              getLanguageDisplayValue={getLanguageDisplayValue}
              i18nLang={i18n.language}
            />
            {(session?.account ||
              payableSession?.payable?.category?.toLowerCase() === "account") &&
              !props.isMobile &&
              session?.account?.channel !== "CallCenter" &&
              payableSession?.channel !== "CallCenter" && (
                <TextButton
                  icon={<FontAwesomeIcon icon={faSignOut} />}
                  onClick={() => handleLogoutClick()}
                  dataCy="button-logout"
                >
                  {t("change-account", { ns: MENU_NAMESPACE })}
                </TextButton>
              )}
            {(session?.account ||
              payableSession?.payable?.category?.toLowerCase() === "account") &&
              props.isMobile &&
              session?.account?.channel !== "CallCenter" &&
              payableSession?.channel !== "CallCenter" && (
                <StyledChangeAccountMobileButton
                  icon={<FontAwesomeIcon icon={faSignOut} />}
                  onClick={() => handleLogoutClick()}
                  dataCy="button-logout"
                >
                  {""}
                </StyledChangeAccountMobileButton>
              )}
          </div>
        </div>

        {/* about ez-pay modal and sheet */}
        {props.isMobile && (
          // @ts-ignore
          <CustomSheet
            isOpen={aboutModalDisplay}
            onClose={() => setAboutModalIsOpenToFalse}
            detent="content-height"
            disableDrag={true}
          >
            <CustomSheet.Container>
              <CustomSheet.Header>
                <div>
                  <button
                    className="modal-close-mobile"
                    data-cy="modal-close-x-about"
                    onClick={setAboutModalIsOpenToFalse}
                  >
                    <img
                      src={XButton}
                      alt="close modal window"
                      className="modal-close-x"
                    />
                  </button>
                </div>
              </CustomSheet.Header>
              <CustomSheet.Content>
                <AboutModal closeCallBack={setAboutModalIsOpenToFalse} />
              </CustomSheet.Content>
            </CustomSheet.Container>
            {/* @ts-ignore */}
            <Sheet.Backdrop onClick={setAboutModalIsOpenToFalse} />
          </CustomSheet>
        )}
        {!props.isMobile && (
          // @ts-ignore
          <Modal
            isOpen={aboutModalDisplay}
            ariaHideApp={false}
            className="modal-frame-about"
            shouldCloseOnOverlayClick={true}
            onRequestClose={setAboutModalIsOpenToFalse}
            style={customStyles}
          >
            <div>
              <button
                className="modal-close"
                onClick={setAboutModalIsOpenToFalse}
                data-cy="modal-close-x-about"
                style={{ background: "#C4E7FF" }}
              >
                <img
                  src={XButton}
                  alt="close modal window"
                  className="modal-close-x"
                />
              </button>
            </div>
            <AboutModal closeCallBack={setAboutModalIsOpenToFalse} />
          </Modal>
        )}

        {/* help modal and sheet */}
        {props.isMobile && (
          <Sheet
            isOpen={helpModalDisplay}
            onClose={() => setHelpModalIsOpenToFalse}
            detent="content-height"
            disableDrag={true}
          >
            <Sheet.Container>
              <Sheet.Header>
                <div className="react-modal-sheet-header">
                  <div className="help-modal-header-text">
                    {t("help", { ns: COMMON_NAMESPACE })}
                  </div>
                  <button
                    className="modal-close"
                    onClick={setHelpModalIsOpenToFalse}
                    data-cy="modal-close-x-help"
                  >
                    <img
                      src={XButton}
                      alt="close modal window"
                      className="modal-close-x"
                    />
                  </button>
                </div>
              </Sheet.Header>
              <Sheet.Content>
                <HelpModal />
              </Sheet.Content>
            </Sheet.Container>
            {/* @ts-ignore */}
            <Sheet.Backdrop onClick={setHelpModalIsOpenToFalse} />
          </Sheet>
        )}
        {!props.isMobile && (
          // @ts-ignore
          <Modal
            isOpen={helpModalDisplay}
            ariaHideApp={false}
            className="modal-frame-help"
            shouldCloseOnOverlayClick={true}
            onRequestClose={setHelpModalIsOpenToFalse}
            style={customStyles}
          >
            <div className="modal-header">
              <div className="help-modal-header-text">
                {t("help", { ns: COMMON_NAMESPACE })}
              </div>
              <button
                className="modal-close"
                onClick={setHelpModalIsOpenToFalse}
                data-cy="modal-close-x-help"
              >
                <img
                  src={XButton}
                  alt="close modal window"
                  className="modal-close-x"
                />
              </button>
            </div>
            <HelpModal />
          </Modal>
        )}
      </div>
      {location.pathname === routing.index() && (
        <WelcomeMessageSection biller={biller} isMobile={props.isMobile} />
      )}
      <Outlet context={session || payableSession} />
    </>
  );
};
